/**
 * NotifPermissionPrompt — 전역 알림 권한 프롬프트/안내 모달.
 * window 이벤트 "open-notif-permission" 로 어디서든(종 아이콘 등) 열 수 있다.
 * - permission === "default" → "알림 켜시겠습니까?" (허용 → requestPermission)
 * - permission === "denied"  → "차단되어 있어요" + 브라우저에서 켜는 법 안내 (브라우저가 재요청 금지)
 * 로그인 플로우의 인라인 프롬프트와 별개(그건 로그인 완료 처리를 겸함).
 */
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Bell } from "lucide-react";
import { registerFcmToken } from "../../firebase";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";
import { useBackClose } from "../../hooks/useBackClose";

export const NOTIF_PROMPT_EVENT = "open-notif-permission";

export default function NotifPermissionPrompt() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  useBodyScrollLock(containerRef, open);

  useEffect(() => {
    const h = () => setOpen(true);
    window.addEventListener(NOTIF_PROMPT_EVENT, h);
    return () => window.removeEventListener(NOTIF_PROMPT_EVENT, h);
  }, []);

  // 요구사항 #550: 안드로이드 뒤로가기로 알림권한 프롬프트 닫기 (pushState 방식)
  useBackClose(() => setOpen(false), open);

  if (!open) return null;

  const denied = typeof Notification !== "undefined" && Notification.permission === "denied";
  const close = () => setOpen(false);
  const markDismiss = () => {
    try { localStorage.setItem("notif_prompt_dismissed_at", String(Date.now())); } catch { /* private */ }
  };

  return (
    <div ref={containerRef} className="fixed inset-0 z-[300] flex items-center justify-center bg-black/40 px-4" onClick={close}>
      <div
        className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex justify-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-amber-500/15">
            <Bell className="h-7 w-7 text-amber-400" />
          </div>
        </div>
        {denied ? (
          <>
            <h3 className="mb-2 text-base font-bold text-slate-900">{t("notif.blocked_title")}</h3>
            <p className="mb-5 whitespace-pre-line text-left text-xs leading-relaxed text-slate-500">{t("notif.blocked_desc")}</p>
            <button
              onClick={() => { markDismiss(); close(); }}
              className="w-full rounded-xl bg-gradient-to-r from-amber-500 to-orange-300 px-4 py-2.5 text-sm font-bold text-slate-900 shadow-lg shadow-amber-500/25 transition-all hover:brightness-110"
            >
              {t("notif.blocked_ok")}
            </button>
          </>
        ) : (
          <>
            <h3 className="mb-2 text-base font-bold text-slate-900">{t("notif.prompt_title")}</h3>
            <p className="mb-6 text-xs leading-relaxed text-slate-400">{t("notif.prompt_desc")}</p>
            <div className="flex gap-3">
              <button
                onClick={() => { markDismiss(); close(); }}
                className="flex-1 rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-400 transition-all hover:bg-slate-100 hover:text-slate-900"
              >
                {t("notif.later")}
              </button>
              <button
                onClick={async () => {
                  markDismiss();
                  try {
                    await Promise.race([
                      registerFcmToken(),
                      new Promise((r) => setTimeout(r, 3000)),
                    ]);
                  } catch { /* noop */ }
                  close();
                }}
                className="flex-1 rounded-xl bg-gradient-to-r from-amber-500 to-orange-300 px-4 py-2.5 text-sm font-bold text-slate-900 shadow-lg shadow-amber-500/25 transition-all hover:brightness-110"
              >
                {t("notif.allow")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
